import { useNavigate } from "react-router-dom";
import { authErrorMessage, useAuth } from "@/auth";
type PasswordReset = { email: string };
import { ToastHost, useToast } from "@/utility/toast-utility";
import {
  Box,
  Button,
  Text,
  Group,
  TextInput,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { useFormik } from "formik";
import * as Yup from "yup";


export default function ForgotPassword() {
  const navigate = useNavigate();
  const auth = useAuth();


  const { closeToast, showToast, toast } = useToast();

  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();

  const formik = useFormik<PasswordReset>({
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Please enter a valid email address")
        .required("Email is required")
        .matches(/^[^\s]+$/, "Email cannot contain spaces")
        .matches(/^[a-zA-Z0-9@._-]+$/, "Email contains unsupported symbols")
        .label("Email"),
    }),
    onSubmit: async (values) => {
      // Firebase emails a reset link; the same message shows whether or not the
      // address exists, so the form cannot be used to probe for accounts.
      try {
        await auth.resetPassword(values.email.trim());
      } catch (err) {
        const code = typeof err === "object" && err && "code" in err ? String((err as { code: unknown }).code) : "";
        if (code !== "auth/user-not-found") {
          showToast({ type: "error", message: authErrorMessage(err) });
          return;
        }
      }
      showToast({ type: "success", message: "If that email has an account, a reset link is on its way." });
      setTimeout(() => navigate("/auth/login"), 2500);
    },
  });
  return (
    <Box
      mt={{ base: "md", sm: "lg" }}
      style={{
        // alignItems: "center",
        // justifyContent: "center",
        margin: "0 auto",
        borderRadius: "20px",
      }}
      w={{ base: "100%", sm: 628 }}
      maw={{ base: "100%", sm: 628 }}
      bg={
        colorScheme === "dark"
          ? theme.colors.neutral[8]
          : "rgb(244, 244, 244)"
      }
      // p={{ base: 16, sm: 25 }}
      pr={{ base: 56, sm: 50 }}
      mx={{ base: 16, sm: "auto" }}
    >
      <ToastHost toast={toast} onClose={closeToast} />
      <Text
        ta="center"
        mt={{ base: "sm", sm: "md" }}
        size="xl"
        fw={600}
        c={
          colorScheme === "dark"
            ? theme.colors.neutral[2]
            : theme.colors.neutral[9]
        }
      >
        Forgot Password
      </Text>

      <Text
        ta="center"
        mt="xs"
        size="md"
        c={
          colorScheme === "dark"
            ? theme.colors.neutral[4]
            : theme.colors.neutral[6]
        }
      >
        Enter your email address and we'll send you a link to reset your password
      </Text>

      <TextInput
        mt={{ base: "lg", sm: "xl" }}
        withAsterisk
        name="email"
        label="Email"
        placeholder="Enter Email"
        size="md"
        value={formik.values.email}
        onChange={formik.handleChange}
        error={formik.touched.email && formik.errors.email}
        styles={{
          label: {
            fontSize: "0.875rem",
            fontWeight: 500,
            marginBottom: 8,
          },
          input: {
            height: 40,
            fontSize: "0.9rem",
          },
        }}
      />

      <Group w={"100%"} mt="xl" gap="sm" wrap="wrap">
        <Button
          size="md"
          variant="outline"
          onClick={() => navigate("/auth/login")}
          style={{ 
            flex: 1, 
            minWidth: 120,
            borderColor: colorScheme === "dark" ? theme.colors.neutral[6] : "#E9E9E9",
            color: colorScheme === "dark" ? theme.colors.neutral[2] : "#101828",
            backgroundColor: colorScheme === "dark" ? theme.colors.neutral[7] : "transparent",
          }}
          styles={{
            root: {
              "&:hover": {
                backgroundColor: colorScheme === "dark" ? theme.colors.neutral[6] : "#E9E9E9",
                borderColor: colorScheme === "dark" ? theme.colors.neutral[6] : "#E9E9E9",
              },
            },
          }}
        >
          Cancel
        </Button>

        <Button
          size="md"
          onClick={() => formik.handleSubmit()}
          style={{ flex: 1, minWidth: 120 }}
          type="submit"
          loading={formik.isSubmitting}
        >
          Submit
        </Button>
      </Group>
    </Box>
  );
}
